<template>
  <div>
    <b-breadcrumb :items="breadcrumbItems"></b-breadcrumb>

    <div class="row mb-3">
      <div class="col-sm-4">
        <h4 qid="measures-page-title" class="mb-2">
          {{ $t('maintenance.incidents.incident_categories.title') }}
        </h4>
        <div>
          <span v-if="getGuide('incident_category_list_page')">
            <GuideButton :guide="getGuide('incident_category_list_page')"></GuideButton>
          </span>
        </div>
      </div>
      <div class="col-sm-8 text-right">
        <button
            class="btn btn-secondary-dark float-right"
            @click="filterOpened = !filterOpened"
            qid="measures-create-button"
        >
          <font-awesome-icon icon="filter"/>
        </button>
        <button
            class="btn btn-success float-right"
            @click="showCreateModal"
            qid="measures-create-button"
            v-if="isGranted(permissions.INCIDENT_CATEGORY_CREATE)"
        >
          <font-awesome-icon icon="plus-circle"/> {{ $t('maintenance.incidents.incident_categories.create_button') }}
        </button>
        <b-form-input
          type="text"
          v-model="filters.keywordValue"
          class="float-right mw-300 mr-3 search-bar"
          qid="search-p-input"
          v-on:input="debounceInput"
          :placeholder="$t('maintenance.incidents.incident_categories.search_placeholder')"
        ></b-form-input>
      </div>
    </div>

    <div class="row" :class="{'table-with-filters': filterOpened}">
      <div class="col-sm-10 table-container">
        <div class="card">
          <table class="table table-responsive-md">
            <thead>
            <tr>
              <th scope="col"
                  class="cursor-pointer"
                  @click="selectSort('name')"
                  qid="sort-p-name"
              >
                {{ $t('table.name') }}
                <span class="ml-2">
                  <font-awesome-icon class="text-muted" icon="sort" v-if="selectedSort !== 'name'"/>
                  <font-awesome-icon icon="caret-down" v-if="selectedDirection === 'desc' && selectedSort === 'name'"/>
                  <font-awesome-icon icon="caret-up" v-if="selectedDirection === 'asc' && selectedSort === 'name'"/>
                </span>
              </th>
              <th scope="col">
                {{ $t('table.type') }}
              </th>
              <th scope="col">
                {{ $t('table.responsibles') }}
              </th>
              <th scope="col">
                {{ $t('maintenance.incidents.incident_categories.project_tasks_label') }}
              </th>
              <th width="200" class="text-right"></th>
            </tr>
            </thead>
            <tbody>
              <tr v-for="object in tableItems">
                <td>
                  <router-link
                      :to="{ name: 'maintenance.incident_categories.view', params: { slug: object.slug }}"
                      qid="p-view-page-link"
                  >
                    {{ object.name }}
                  </router-link>
                </td>
                <td>
                  <span v-if="object.type">
                    <span class="mr-1 badge">
                      {{ $t('maintenance.incidents.incident_categories.type.' + $options.filters.slugify(object.type)) }}
                    </span>
                  </span>
                </td>
                <td>
                  <span class="badge" v-if="object.responsible_department && object.responsible_department.id">
                    <font-awesome-icon icon="sitemap"/>
                    {{ object.responsible_department.name }}
                  </span>
                  <span v-if="object.users && object.users.length">
                    <span class="mr-1 text-normal" v-for="user in getReducedUsers(object.users)">
                      <span qid="profile-photo">
                        <Avatar :object="user" size="xs"></Avatar>
                      </span>
                    </span>
                    <span v-if="object.users.length > 3"
                          class="badge cursor-pointer"
                          @click="openUsersModal(object)"
                    >
                      <font-awesome-icon icon="users"/>
                      + {{ object.users.length - 3 }}
                    </span>
                  </span>
                </td>
                <td>

                </td>
                <td class="text-right">
                  <span v-if="isGranted([permissions.INCIDENT_CATEGORY_EDIT, permissions.INCIDENT_CATEGORY_EDIT_OWN], object.created_by) && (!getClient.parent || (getClient.parent && object.partition))"
                        class="btn btn-edit"
                        qid="edit-p-modal"
                        @click="showEditPModal(object)"
                  >
                    <font-awesome-icon icon="pen"/>
                  </span>
                  <span v-if="isGranted([permissions.INCIDENT_CATEGORY_DELETE, permissions.INCIDENT_CATEGORY_DELETE_OWN], object.created_by) && (!getClient.parent || (getClient.parent && object.partition))"
                        class="btn btn-delete"
                        qid="delete-p-modal"
                        @click="showConfirmDeleteModal(object)"
                  >
                    <font-awesome-icon icon="trash-alt"/>
                  </span>
                </td>
              </tr>
              <tr v-if="!loading && !tableItems.length" qid="no-results-row" class="text-center">
                <td colspan="10">
                  <h5 class="mt-3">
                    {{ $t('system.no_results') }}
                  </h5>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="mt-3 pagination row">
          <div class="col-md-6">
            <div class="row">
              <div class="float-left">
                <span class="text-muted pagination-counter">
                  {{ $t('pagination.entries_per_page') }}:
                </span>
              </div>
              <div class="float-left">
                <multiselect
                    v-model="perPage"
                    :options="perPageOptions"
                    track-by="value"
                    :select-label="$t('system.dropdown_select_label')"
                    :selected-label="$t('system.dropdown_selected_label')"
                    :deselect-label="$t('system.dropdown_deselect_label')"
                    label="value"
                    qid="create-org-form-organization-level"
                    @select="perPageChanged"
                    :placeholder="selectedPerPage"
                    @remove="perPageRemoved"
                ></multiselect>
              </div>
            </div>
          </div>
          <div class="col-md-6">
            <b-pagination
                class="float-right"
                v-model="currentPage"
                :total-rows="rows"
                :per-page="selectedPerPage"
                aria-controls="my-table"
                @change="pageChanged"
                limit="1"
            >
              <span class="text-muted" slot="first-text"><font-awesome-icon icon="angle-double-left"/></span>
              <span class="text-muted" slot="prev-text"><font-awesome-icon icon="angle-left"/></span>
              <span class="text-muted" slot="next-text"><font-awesome-icon icon="angle-right"/></span>
              <span class="text-muted" slot="last-text"><font-awesome-icon icon="angle-double-right"/></span>
              <div slot="ellipsis-text">
                <b-spinner small type="grow"></b-spinner>
                <b-spinner small type="grow"></b-spinner>
                <b-spinner small type="grow"></b-spinner>
              </div>
              <span class="text-muted" slot="page" slot-scope="{ page, active }">
                <span v-if="active">{{getPerPage}}</span>
              </span>
            </b-pagination>
          </div>
        </div>
      </div>
      <div class="col-sm-2 filter-container">
        <div class="card">
          <div class="card-body">
            <b-form-group>
              <label qid="filter-p-type-label">
                {{ $t('table.type') }}
              </label>
              <multiselect
                  v-model="filters.type"
                  :options="types"
                  :placeholder="$t('table.type')"
                  @select="debounceInput"
                  @remove="debounceInput"
                  select-label="✔"
                  selected-label="✔"
                  deselect-label="x"
                  track-by="name"
                  label="name"
                  qid="filter-p-form-process_group"
              ></multiselect>
            </b-form-group>
            <a @click="resetFilters" class="text-right" qid="filter-p-reset">
              {{ $t('system.reset_filters') }}
            </a>
          </div>
        </div>
      </div>
    </div>


    <!-- Delete Policy confirm modal template -->
    <b-modal ref="delete-category" hide-footer>
      <div class="d-block text-center">
        <h4 qid="delete-p-title">
          {{ $t('maintenance.incidents.incident_categories.delete.title') }}
        </h4>
      </div>
      <h5 class="text-center">
        {{ $t('maintenance.incidents.incident_categories.delete.description') }} {{ itemToDelete.name }}
      </h5>
      <h5 class="text-center">
        {{ $t('maintenance.incidents.incident_categories.delete.are_you_sure') }}
      </h5>
      <div class="text-center">
        <b-button class="mt-3"
                  variant="secondary"
                  @click="hideConfirmDeleteModal"
                  qid="delete-p-cancel-button"
        >
          {{ $t('maintenance.incidents.incident_categories.delete.cancel') }}
        </b-button>
        <b-button @click="confirmDeleteP(itemToDelete)"
                  class="mt-3 ml-3"
                  variant="success"
                  qid="delete-p-submit-button"
                  v-if="isGranted([permissions.INCIDENT_CATEGORY_DELETE, permissions.INCIDENT_CATEGORY_DELETE_OWN], itemToDelete.created_by) && (!getClient.parent || (getClient.parent && itemToDelete.partition))"
        >
          {{ $t('maintenance.incidents.incident_categories.delete.submit') }}
        </b-button>
      </div>
    </b-modal>


    <!-- Edit incident category modal template -->
    <b-modal ref="edit-category" hide-footer>
      <div class="d-block text-center">
        <h4 qid="update-p-title">
          {{ $t('maintenance.incidents.incident_categories.update.title') }}
        </h4>
      </div>
      <b-alert v-if="error.length"
               show
               variant="danger"
               qid="edit-p-error"
      >
        {{ error }}
      </b-alert>
      <b-form @submit="editPFormSubmit" qid="edit-p-form">
        <div class="form-container">
          <b-form-group>
            <label qid="edit-p-name-label">
              {{ $t('maintenance.incidents.incident_categories.create.name_label') }}
            </label>
            <span class="text-red">*</span>
            <b-form-input
              qid="edit-p-form-name"
              type="text"
              v-model="pForm.name"
              :maxLength="255"
              required
              :placeholder="$t('maintenance.incidents.incident_categories.create.name_placeholder')"
            ></b-form-input>
          </b-form-group>
          <b-form-group>
            <label qid="edit-p-implementation-status-label">
              {{ $t('maintenance.incidents.incident_categories.create.type_label') }}
            </label>
            <span class="text-red">*</span>
            <multiselect
                v-model="selectedType"
                :options="types"
                track-by="value"
                :placeholder="$t('maintenance.incidents.incident_categories.create.type_placeholder')"
                label="name"
                :select-label="$t('system.dropdown_select_label')"
                :selected-label="$t('system.dropdown_selected_label')"
                :deselect-label="$t('system.dropdown_deselect_label')"
                qid="edit-p-form-implementation-status"
            ></multiselect>
          </b-form-group>
        </div>
        <div class="text-right">
          <b-button class="mt-3"
                    variant="secondary"
                    @click="hideEditPModal"
                    qid="edit-p-cancel-button"
          >
            {{ $t('maintenance.measures.create.cancel') }}
          </b-button>
          <b-button type="submit"
                    class="mt-3 ml-3"
                    variant="success"
                    :disabled="buttonDisabled"
                    qid="edit-p-submit-button"
          >
            <span v-if="buttonDisabled" class="mr-1">
              <font-awesome-icon icon="spinner" size="sm" class="fa-spin"/>
            </span>
            {{ $t('maintenance.measures.create.submit') }}
          </b-button>
        </div>
      </b-form>
    </b-modal>


    <!-- Create incident category -->
    <b-modal ref="create-category" modal-class="task-modal">

      <template #modal-header="{ close }">
        <div class="" style="width: 100%;">
          <span @click="hideCreateModal" class="btn btn-secondary cursor-pointer float-right">
            <font-awesome-icon icon="times"/>
          </span>
          <h5 class="mb-1 text-center">
            {{ $t('maintenance.incidents.incident_categories.create.title') }}
          </h5>
          <div class="row mt-3 pt-4 pb-1 pl-5 pr-5">
            <div class="col-sm-12"
                 v-if="getGuide('create_incident_category')"
            >
              <b-form-group>
                <div class="">
                  <label qid="create-p-users-label">
                    <font-awesome-icon icon="info-circle"/>
                    {{ $t('system.help_center_label') }}
                  </label>
                  <div v-if="wpGuide && wpGuide.id" class="guide-content-container">
                    <p v-if="!showFullGuideDescription" v-html="wpGuide.excerpt.rendered"></p>
                    <p v-if="showFullGuideDescription" v-html="wpGuide.content.rendered"></p>

                    <div class="text-center mt-3" @click="showFullGuideDescription=!showFullGuideDescription">
                      <span class="cursor-pointer" v-if="!showFullGuideDescription">
                        <span class="mr-2">
                          {{ $t('system.view_more') }}
                        </span>
                        <font-awesome-icon icon="chevron-down"/>
                      </span>
                      <span class="cursor-pointer" v-if="showFullGuideDescription">
                        <span class="mr-2">
                          {{ $t('system.view_less') }}
                        </span>
                        <font-awesome-icon icon="chevron-up"/>
                      </span>
                    </div>
                  </div>
                </div>
              </b-form-group>
            </div>
          </div>
        </div>

      </template>

      <b-alert v-if="error.length"
               show
               variant="danger"
               qid="edit-p-error"
      >
        {{ error }}
      </b-alert>
      <b-form @submit="createFormSubmit" qid="edit-p-form">
        <div class="form-container">
          <b-form-group>
            <label qid="edit-p-implementation-status-label">
              {{ $t('maintenance.incidents.incident_categories.create.type_label') }}
            </label>
            <span class="text-red">*</span>
            <multiselect
                v-model="selectedType"
                :options="types"
                track-by="value"
                :placeholder="$t('maintenance.incidents.incident_categories.create.type_placeholder')"
                label="name"
                :select-label="$t('system.dropdown_select_label')"
                :selected-label="$t('system.dropdown_selected_label')"
                :deselect-label="$t('system.dropdown_deselect_label')"
                qid="edit-p-form-implementation-status"
            ></multiselect>
          </b-form-group>
          <b-form-group class="mt-3">
            <label qid="edit-p-name-label">
              {{ $t('maintenance.incidents.incident_categories.create.name_label') }}
            </label>
            <span class="text-red">*</span>
            <b-form-input
              qid="edit-p-form-name"
              type="text"
              v-model="pForm.name"
              :maxLength="255"
              required
              :placeholder="$t('maintenance.incidents.incident_categories.create.name_placeholder')"
            ></b-form-input>
          </b-form-group>
          <b-form-group>
            <label qid="create-p-description-label">
              {{ $t('maintenance.policies.create.description_label') }}
            </label>
            <b-form-textarea
              qid="create-p-form-description"
              v-model="pForm.description"
              rows="6"
              max-rows="6"
              :placeholder="$t('maintenance.policies.create.description_placeholder')"
            ></b-form-textarea>
          </b-form-group>
        </div>
      </b-form>

      <template #modal-footer="{ close }">
        <div class="" style="width: 100%;">
          <div class="alert alert-menu-color color-secondary mb-0" role="alert">
            <div class="text-right">
              <b-button class="mt-3"
                        variant="secondary"
                        @click="hideCreateModal"
                        qid="edit-p-cancel-button"
              >
                {{ $t('maintenance.measures.create.cancel') }}
              </b-button>
              <b-button type="submit"
                        class="mt-3 ml-3"
                        variant="success"
                        :disabled="buttonDisabled"
                        @click="createFormSubmit()"
                        qid="edit-p-submit-button"
              >
                <span v-if="buttonDisabled" class="mr-1">
                  <font-awesome-icon icon="spinner" size="sm" class="fa-spin"/>
                </span>
                {{ $t('maintenance.measure_categories.create.submit') }}
              </b-button>
            </div>
          </div>
        </div>

      </template>

    </b-modal>


    <ShowMoreModal
        :items="selectedItem.users"
        :show="showUsersModal"
        @closeViewModal="closeUsersModal"
        type="users"
    ></ShowMoreModal>

  </div>
</template>

<script>
  import piincHttp from '@/util/configureAxios.js';
  import ShowMoreModal from '@/components/ShowMoreModal.vue';
  import GuideButton from '@/components/guide/GuideButton.vue';
  import Avatar from '@/components/Avatar.vue';

  export default {
    name: 'IncidentCategories',
    data: function() {
      return {
        breadcrumbItems: [
          {
            text: this.$t('dashboard.title'),
            to: '/'
          },
          {
            text: this.$t('maintenance.title'),
            to: '/maintenance'
          },
          {
            text: this.$t('maintenance.incidents.incident_categories.title'),
            active: true
          }
        ],
        tableItems: [],

        // Pagination
        perPageOptions: [
          { name: '10', value: '10' },
          { name: '20', value: '20' },
          { name: '50', value: '50' },
          { name: '100', value: '100' }
        ],
        perPage: '',
        selectedPerPage: "10",
        currentPage: 1,
        totalRows: 0,

        itemToDelete: {},
        pForm: {
          name: '',
          description: '',
          type: []
        },
        error: '',
        selectedSort: '',
        selectedDirection: '',
        filterOpened: false,
        keywordValue: '',
        filters: {
          type: '',
          keywordValue: ''
        },
        categoryItems: [],
        categoryInput: '',
        types: [
          { name: this.$t('maintenance.incidents.incident_categories.type.incident'), value: 'incident' },
          { name: this.$t('maintenance.incidents.incident_categories.type.service_request'), value: 'service_request' },
        ],
        loading: true,
        buttonDisabled: false,
        selectedCategory: {},
        selectedType: [],
        selectedItem: {},
        showFullGuideDescription: false,
        wpGuide: {},
        myGuides: [],
        showUsersModal: false
      }
    },
    components: {
      ShowMoreModal,
      GuideButton,
      Avatar
    },
    computed: {
      rows() {
        // Use computed value for rows because of dynamic update
        return this.totalRows
      },
      getClient() {
        return this.$store.state.client
      },
      getFilters() {
        return this.$store.state.activeFilters
      },
      getStorePerPage() {
        return this.$store.state.perPage
      },
      getWordpressLink() {
        return this.$store.state.wordpressLink
      },
      getPerPage() {
        let page = this.currentPage;
        let selectedFrom = (page-1) * Number(this.selectedPerPage) + 1;
        let selectedTo = (page-1) * Number(this.selectedPerPage) + Number(this.selectedPerPage);
        let selectedOf = this.totalRows;

        if(selectedTo > selectedOf){
          selectedTo = selectedOf
        }

        return selectedFrom + " - " + selectedTo + " " + this.$t('system.of') + " " + selectedOf
      }
    },
    watch: {
      // Watch when client changes in store and trigger api reload
      getClient: function () {
        this.loadContent();
      }
    },
    methods: {
      debounceInput: _.debounce(function() {
        this.loadContent()
      }, 400),
      getGuide(code) {
        let existingGuide = _.find(this.myGuides, function(object) {
          return object.scope == code && object.type == 'Incident' && object.enabled
        });
        return existingGuide
      },
      loadGuides(code) {
        let self = this;
        let language = localStorage.getItem('lang')? localStorage.getItem('lang') : 'en';

        piincHttp.get('guides', { params:
          {
            per_page: 1,
            language: 'en',
            type: 'Incident',
            scope: code,
            client: this.getClient.slug
          }
        }).then(function(response) {
          if (response.data.items.length) {
            self.myGuides.push(response.data.items[0])
            piincHttp.get(self.getWordpressLink + self.myGuides[0].external_id).then(function(response) {
              self.wpGuide = response.data
            }, function() {});
          }
        }, function() {});
      },
      resetFilters() {
        this.filters.type = '',
        this.debounceInput()
      },
      openUsersModal(object) {
        this.selectedItem = object
        this.showUsersModal = true
      },
      closeUsersModal() {
        this.selectedItem = {}
        this.showUsersModal = false
      },
      getReducedUsers(users) {
        let reducedUsers = []
        if (users.length > 3) {
          reducedUsers = _.cloneDeep(users);
          reducedUsers = reducedUsers.slice(0, 3);
        } else {
          reducedUsers = users;
        }
        return reducedUsers;
      },
      handleCategoryInput(input) {
        this.categoryInput = input;
      },
      selectSort(sort) {
        // If old and new sort are same, switch just direction
        if (sort === this.selectedSort) {
          this.selectedDirection = this.selectedDirection === 'desc' ? 'asc' : 'desc';
        } else {
          // If different sort, set sort and set direction to default
          this.selectedDirection = 'desc'
          this.selectedSort = sort;
        }
        this.loadContent();
      },
      loadContent(page, perPage) {

        if (this.getStorePerPage) {
          this.selectedPerPage = this.getStorePerPage;
        }

        // If page is not defined, set page to default 1
        let pageNumber = page ? page : 1;
        let perPageNumber = perPage ? perPage : this.selectedPerPage;
        let self = this;

        if (
          this.filters.keywordValue !== '' ||
          (this.filters.type && this.filters.type.value)
        ) {
          this.filtersOpened = true
        }

        piincHttp.get('incident-categories', { params:
          {
            page: pageNumber,
            per_page: perPageNumber,
            sort: this.selectedSort !== '' ? this.selectedSort : undefined,
            order: this.selectedDirection !== '' ? this.selectedDirection : undefined,
            keyword: this.filters.keywordValue !== '' ? this.filters.keywordValue : undefined,
            type: this.filters.type ? this.filters.type.value : undefined,
            client: this.getClient.slug
          }
        }).then(function(response) {
          self.loading = false;
          self.tableItems = response.data.items;
          // Set number of total items
          self.totalRows = response.data.total_count;
        }, function() {});
      },
      pageChanged(page) {
        this.loadContent(page)
      },
      perPageChanged(perPage) {
        let self = this;
        // Load content when page changed listener is triggered
        this.loadContent(1, perPage.value)
        self.selectedPerPage = perPage.value
        this.$store.commit('storePerPage', this.selectedPerPage);
      },
      perPageRemoved() {
        let self = this;
        // Load content when page changed listener is triggered
        self.selectedPerPage = self.perPageOptions[0].value
        this.loadContent(1, self.selectedPerPage)
      },
      showConfirmDeleteModal(object) {
        this.itemToDelete = object
        this.$refs['delete-category'].show()
      },
      hideConfirmDeleteModal() {
        this.itemToDelete = {}
        this.$refs['delete-category'].hide()
      },
      showViewModal(object) {
        this.selectedCategory = object
        this.$refs['view-category'].show()
      },
      hideViewModal() {
        this.selectedCategory = {}
        this.$refs['view-category'].hide()
      },
      showCreateModal() {
        this.pForm.name = ''
        this.pForm.description = ''
        this.pForm.type = []
        this.selectedType = []
        this.error = ''
        this.$refs['create-category'].show()
      },
      hideCreateModal() {
        this.pForm.name = ''
        this.pForm.description = ''
        this.pForm.type = []
        this.selectedType = []
        this.$refs['create-category'].hide()
      },
      createFormSubmit(evt) {
        this.buttonDisabled = true;
        // Keep the reference in a variable
        // to use it inside function called when promise returned
        let self = this;

        if (evt) {
          evt.preventDefault();
        }

        this.pForm.client = this.getClient.slug;

        if (this.selectedType && this.selectedType.value) {
          this.pForm.type = this.selectedType.value
        }

        if (this.selectedType && this.selectedType.value && this.pForm.name && this.pForm.name.length) {
          piincHttp.post('incident-categories', this.pForm).then(function(response) {
            self.hideCreateModal();
            self.loadContent(self.currentPage);
            self.buttonDisabled = false;
            self.$toastr('success', self.$t('maintenance.incidents.incident_categories.create.success'))
          }, function(error) {
            if (error.status !== 200) {
              self.buttonDisabled = false;
              self.error = error.data.message;
            }
          })
        } else {
          this.buttonDisabled = false;
          this.error = ''
          if (!this.selectedType || (this.selectedType && !this.selectedType.value)) {
            this.error += this.$t('maintenance.incidents.incident_categories.create.type_error')
          }
          if (this.pForm.name == '') {
            this.error +=  ' ' + this.$t('maintenance.incidents.incident_categories.create.name_error')
          }

        }
      },
      showEditPModal(object) {
        let self = this;

        self.selectedType = [];
        this.error = ''

        if (object.type) {
          this.selectedType = _.find(this.types, function(item) {
            return item.value === object.type;
          });
        }

        this.pForm.slug = object.slug;
        this.pForm.name = object.name;
        this.$refs['edit-category'].show()
      },
      hideEditPModal() {
        this.error = ''
        this.$refs['edit-category'].hide()
      },
      editPFormSubmit(evt) {
        this.buttonDisabled = true;
        // Keep the reference in a variable
        // to use it inside function called when promise returned
        let self = this;
        evt.preventDefault();

        if (this.selectedType && this.selectedType.value) {
          this.pForm.type = this.selectedType.value
        }

        this.pForm.client = this.getClient.slug;

        if (this.selectedType && this.selectedType.value) {
          piincHttp.put('incident-categories/' + this.pForm.slug, this.pForm).then(function(response) {
            self.hideEditPModal();
            self.loadContent(self.currentPage);
            self.buttonDisabled = false;
            self.$toastr('success', self.$t('maintenance.incidents.incident_categories.update.success'))
          }, function(error) {
            if (error.status !== 200) {
              self.buttonDisabled = false;
              self.error = error.data.message;
            }
          })
        } else {
          this.buttonDisabled = false;
          this.error = ''
          if (!this.selectedType || (this.selectedType && !this.selectedType.value)) {
            this.error += this.$t('maintenance.incidents.incident_categories.create.type_error')
          }
        }
      },
      confirmDeleteP(object) {
        let self = this;
        piincHttp.delete('incident-categories/' + object.slug).then(function(response) {
          self.hideConfirmDeleteModal();
          self.loadContent(self.currentPage);
          // Trigger toastr message after modal close
          self.$toastr('success', self.$t('maintenance.incidents.incident_categories.delete.success'))
        }, function() {});
      }
    },

    beforeRouteLeave(to, from, next) {
      let object = {
        type: 'incident_categories',
        filters: this.filters
      }
      this.$store.commit('storeActiveFilters', object);
      this.$store.commit('storePerPage', this.selectedPerPage);
      next()
    },
    created () {
      if (this.getClient && this.getClient.slug) {
        if (this.getFilters.measure_categories) {
          this.filters = this.getFilters.measure_categories
        }
        this.loadContent();
      }
      this.loadGuides('incident_category_list_page');
      this.loadGuides('create_incident_category');
    }
  }
</script>
